import { AUTO_MODE_LISTS, POLICY_CATEGORIES } from "./copy.js";

// The draft for a tenant with no saved policy: every control unset, so it
// composes to `{}` and reports no changed settings. Anything an admin has not
// chosen must not appear configured.
export const EMPTY_CONTROLS = Object.freeze({
    disableBypass: false,
    autoModeAllowed: true,
    managedRulesOnly: false,
    defaultMode: "",
    startingModel: "",
    restrictModels: false,
    availableModels: [],
    rules: { deny: [], ask: [], allow: [] },
    autoMode: {
        environment: [],
        allow: [],
        softDeny: [],
        hardDeny: [],
        classifyAllShell: false,
    },
    autoModeDefaults: { environment: true, allow: true, softDeny: true, hardDeny: true },
    mcpMode: "open",
    mcpServers: [],
    managedMcpServers: [],
    managedMcpServersAuthorable: true,
    marketplaceMode: "open",
    marketplaces: [],
    blockedMarketplaces: [],
    blockedMarketplacesAuthorable: true,
    customizationSurfaces: [],
    managedHooksOnly: false,
    disableSkillShell: false,
    disableBundledSkills: false,
    disableWorkflows: false,
    companyLogin: false,
    organizationIds: [],
    preservedDocument: {},
    removedPointers: [],
    preserveCustomization: false,
    preserveLogin: false,
    allowedProviders: [],
    forceLoginGatewayUrl: "",
    deniedModels: [],
    availableModelsMatch: "",
    minimumVersion: "",
    allowAllClaudeAiMcps: false,
    disableCommandPluginSources: false,
    disableSideloadFlags: false,
    allowedHttpHookUrls: [],
    httpHookAllowedEnvVars: [],
    companyAnnouncements: [],
    pluginTrustMessage: "",
    disableRemoteControl: false,
    isolatePeerMachines: false,
    sshHostAllowlist: [],
    disableArtifact: false,
    disableBrowserExternalNavigation: false,
    browserExternalPageTools: false,
});

export function cloneControls(value = EMPTY_CONTROLS) {
    return {
        ...value,
        preservedDocument: JSON.parse(JSON.stringify(value.preservedDocument ?? {})),
        removedPointers: [...(value.removedPointers ?? [])],
        rules: {
            deny: [...(value.rules?.deny ?? [])],
            ask: [...(value.rules?.ask ?? [])],
            allow: [...(value.rules?.allow ?? [])],
        },
        autoMode: {
            environment: [...(value.autoMode?.environment ?? [])],
            allow: [...(value.autoMode?.allow ?? [])],
            softDeny: [...(value.autoMode?.softDeny ?? [])],
            hardDeny: [...(value.autoMode?.hardDeny ?? [])],
            classifyAllShell: value.autoMode?.classifyAllShell === true,
        },
        autoModeDefaults: {
            environment: value.autoModeDefaults?.environment !== false,
            allow: value.autoModeDefaults?.allow !== false,
            softDeny: value.autoModeDefaults?.softDeny !== false,
            hardDeny: value.autoModeDefaults?.hardDeny !== false,
        },
        availableModels: [...(value.availableModels ?? [])],
        mcpServers: [...(value.mcpServers ?? [])],
        managedMcpServers: (value.managedMcpServers ?? []).map((server) => ({ ...server })),
        marketplaces: [...(value.marketplaces ?? [])],
        blockedMarketplaces: [...(value.blockedMarketplaces ?? [])],
        customizationSurfaces: [...(value.customizationSurfaces ?? [])],
        organizationIds: [...(value.organizationIds ?? [])],
        sshHostAllowlist: [...(value.sshHostAllowlist ?? [])],
        allowedProviders: [...(value.allowedProviders ?? [])],
        deniedModels: [...(value.deniedModels ?? [])],
        allowedHttpHookUrls: [...(value.allowedHttpHookUrls ?? [])],
        httpHookAllowedEnvVars: [...(value.httpHookAllowedEnvVars ?? [])],
        companyAnnouncements: [...(value.companyAnnouncements ?? [])],
    };
}

export function composeManagedAgentConfig(controls) {
    const document = JSON.parse(JSON.stringify(controls.preservedDocument ?? {}));
    const permissions = { ...(document.permissions ?? {}) };
    if (controls.disableBypass) permissions.disableBypassPermissionsMode = "disable";
    if (controls.autoModeAllowed === false) permissions.disableAutoMode = "disable";
    if (controls.defaultMode) permissions.defaultMode = controls.defaultMode;
    for (const kind of ["deny", "ask", "allow"]) {
        if (controls.rules[kind]?.length) permissions[kind] = controls.rules[kind];
    }
    if (Object.keys(permissions).length) document.permissions = permissions;
    else delete document.permissions;
    delete document.allowManagedPermissionRulesOnly;
    if (controls.managedRulesOnly) document.allowManagedPermissionRulesOnly = true;

    delete document.autoMode;
    const autoMode = {};
    for (const [controlKey, { key }] of Object.entries(AUTO_MODE_LISTS)) {
        const entries = (controls.autoMode?.[controlKey] ?? [])
            .map((entry) => entry.trim())
            .filter((entry) => entry && entry !== "$defaults");
        if (entries.length) {
            autoMode[key] = controls.autoModeDefaults?.[controlKey] === false
                ? entries
                : ["$defaults", ...entries];
        }
    }
    if (controls.autoMode?.classifyAllShell) autoMode.classifyAllShell = true;
    if (Object.keys(autoMode).length) document.autoMode = autoMode;

    delete document.model;
    delete document.availableModels;
    delete document.enforceAvailableModels;
    const startingModel = controls.startingModel?.trim();
    if (startingModel) document.model = startingModel;
    const availableModels = (controls.availableModels ?? []).map((value) => value.trim()).filter(Boolean);
    if (controls.restrictModels && availableModels.length) {
        document.availableModels = availableModels;
        document.enforceAvailableModels = true;
    }
    delete document.deniedModels;
    delete document.availableModelsMatch;
    const deniedModels = (controls.deniedModels ?? []).map((value) => value.trim()).filter(Boolean);
    if (deniedModels.length) document.deniedModels = deniedModels;
    if (controls.restrictModels && availableModels.length && controls.availableModelsMatch === "exact") {
        document.availableModelsMatch = "exact";
    }
    delete document.allowedProviders;
    delete document.forceLoginGatewayUrl;
    const allowedProviders = (controls.allowedProviders ?? []).filter(Boolean);
    if (allowedProviders.length) document.allowedProviders = allowedProviders;
    const gatewayUrl = controls.forceLoginGatewayUrl?.trim();
    if (gatewayUrl && allowedProviders.includes("gateway")) document.forceLoginGatewayUrl = gatewayUrl;
    delete document.minimumVersion;
    const minimumVersion = controls.minimumVersion?.trim();
    if (minimumVersion) document.minimumVersion = minimumVersion;

    delete document.allowedMcpServers;
    delete document.deniedMcpServers;
    delete document.allowManagedMcpServersOnly;
    const mcpSelectors = controls.mcpServers
        .map((serverName) => serverName.trim())
        .filter(Boolean)
        .map((serverName) => ({ serverName }));
    if (controls.mcpMode === "allow" && mcpSelectors.length) {
        document.allowedMcpServers = mcpSelectors;
        document.allowManagedMcpServersOnly = true;
    } else if (controls.mcpMode === "deny" && mcpSelectors.length) {
        document.deniedMcpServers = mcpSelectors;
    } else if (controls.mcpMode === "block") {
        document.allowedMcpServers = [];
        document.allowManagedMcpServersOnly = true;
    }
    if (controls.managedMcpServersAuthorable) {
        delete document.managedMcpServers;
        const managedServers = (controls.managedMcpServers ?? []).filter((server) => server.name?.trim() && server.url?.trim());
        if (managedServers.length) {
            document.managedMcpServers = {};
            for (const server of managedServers) {
                document.managedMcpServers[server.name.trim()] = { url: server.url.trim() };
            }
        }
    }

    if (controls.marketplaceMode !== "preserve") {
        delete document.strictKnownMarketplaces;
        const marketplaces = controls.marketplaces.map((repo) => repo.trim()).filter(Boolean);
        if (controls.marketplaceMode === "allow" && marketplaces.length) {
            document.strictKnownMarketplaces = marketplaces.map((repo) => ({ source: "github", repo }));
        } else if (controls.marketplaceMode === "block") {
            document.strictKnownMarketplaces = [];
        }
    }
    if (controls.blockedMarketplacesAuthorable) {
        delete document.blockedMarketplaces;
        const blockedMarketplaces = (controls.blockedMarketplaces ?? []).map((repo) => repo.trim()).filter(Boolean);
        if (blockedMarketplaces.length) {
            document.blockedMarketplaces = blockedMarketplaces.map((repo) => ({ source: "github", repo }));
        }
    }

    const hasMcpList = (controls.mcpMode === "allow" || controls.mcpMode === "deny") && mcpSelectors.length > 0;
    const surfaces = (controls.customizationSurfaces ?? []).filter(
        (surface) => surface !== "hooks" && (surface !== "mcp" || hasMcpList),
    );
    if (!controls.preserveCustomization) {
        delete document.strictPluginOnlyCustomization;
        if (surfaces.length) document.strictPluginOnlyCustomization = surfaces;
    }
    delete document.allowManagedHooksOnly;
    delete document.disableSkillShellExecution;
    delete document.disableBundledSkills;
    delete document.disableWorkflows;
    if (controls.managedHooksOnly) document.allowManagedHooksOnly = true;
    if (controls.disableSkillShell) document.disableSkillShellExecution = true;
    if (controls.disableBundledSkills) document.disableBundledSkills = true;
    if (controls.disableWorkflows) document.disableWorkflows = true;
    delete document.allowAllClaudeAiMcps;
    delete document.disableCommandPluginSources;
    delete document.disableSideloadFlags;
    delete document.allowedHttpHookUrls;
    delete document.httpHookAllowedEnvVars;
    if (controls.allowAllClaudeAiMcps) document.allowAllClaudeAiMcps = true;
    if (controls.disableCommandPluginSources) document.disableCommandPluginSources = true;
    if (controls.disableSideloadFlags) document.disableSideloadFlags = true;
    const allowedHttpHookUrls = (controls.allowedHttpHookUrls ?? []).map((value) => value.trim()).filter(Boolean);
    if (allowedHttpHookUrls.length) document.allowedHttpHookUrls = allowedHttpHookUrls;
    const httpHookAllowedEnvVars = (controls.httpHookAllowedEnvVars ?? []).map((value) => value.trim()).filter(Boolean);
    if (httpHookAllowedEnvVars.length) document.httpHookAllowedEnvVars = httpHookAllowedEnvVars;
    delete document.companyAnnouncements;
    delete document.pluginTrustMessage;
    const companyAnnouncements = (controls.companyAnnouncements ?? []).map((value) => value.trim()).filter(Boolean);
    if (companyAnnouncements.length) document.companyAnnouncements = companyAnnouncements;
    const pluginTrustMessage = controls.pluginTrustMessage?.trim();
    if (pluginTrustMessage) document.pluginTrustMessage = pluginTrustMessage;
    if (!controls.preserveLogin || controls.companyLogin) {
        delete document.forceLoginMethod;
        delete document.forceLoginOrgUUID;
    }
    if (controls.companyLogin) {
        document.forceLoginMethod = "claudeai";
        const organizationIds = controls.organizationIds.map((value) => value.trim()).filter(Boolean);
        if (organizationIds.length) {
            document.forceLoginOrgUUID = organizationIds.length === 1 ? organizationIds[0] : organizationIds;
        }
    }
    for (const key of ["disableRemoteControl", "isolatePeerMachines", "sshHostAllowlist", "disableArtifact", "enableArtifact", "disableBrowserExternalNavigation", "browserExternalPageTools"]) {
        delete document[key];
    }
    if (controls.disableRemoteControl) document.disableRemoteControl = true;
    if (controls.isolatePeerMachines) document.isolatePeerMachines = true;
    const sshHosts = (controls.sshHostAllowlist ?? []).map((value) => value.trim()).filter(Boolean);
    if (sshHosts.length) document.sshHostAllowlist = sshHosts;
    if (controls.disableArtifact) document.disableArtifact = true;
    if (controls.disableBrowserExternalNavigation) document.disableBrowserExternalNavigation = true;
    if (controls.browserExternalPageTools) document.browserExternalPageTools = "disabled";
    return document;
}

export function sanitizeAgainstCatalog(document, catalog) {
    const copy = JSON.parse(JSON.stringify(document ?? {}));
    if (!catalog?.settings?.length) return { document: copy, removedPointers: [] };
    const accepted = new Set(catalog.settings.map((setting) => setting.pointer));
    const removedPointers = [];
    for (const key of Object.keys(copy)) {
        const pointer = `/${key}`;
        if (!accepted.has(pointer)) {
            delete copy[key];
            removedPointers.push(pointer);
            continue;
        }
        if (copy[key] && typeof copy[key] === "object" && !Array.isArray(copy[key])) {
            const childPointers = [...accepted].filter((candidate) => candidate.startsWith(`${pointer}/`));
            if (childPointers.length) {
                for (const child of Object.keys(copy[key])) {
                    const childPointer = `${pointer}/${child}`;
                    if (!accepted.has(childPointer)) {
                        delete copy[key][child];
                        removedPointers.push(childPointer);
                    }
                }
            }
        }
    }
    return { document: copy, removedPointers };
}

export function controlsFromDocument(document, catalog = null) {
    const next = cloneControls();
    const sanitized = sanitizeAgainstCatalog(document, catalog);
    document = sanitized.document;
    next.removedPointers = sanitized.removedPointers;
    const preservedDocument = JSON.parse(JSON.stringify(document ?? {}));
    const permissions = document?.permissions ?? {};
    next.disableBypass = permissions.disableBypassPermissionsMode === "disable";
    next.autoModeAllowed = permissions.disableAutoMode !== "disable";
    next.defaultMode = typeof permissions.defaultMode === "string" ? permissions.defaultMode : "";
    next.rules = {
        deny: Array.isArray(permissions.deny) ? permissions.deny : [],
        ask: Array.isArray(permissions.ask) ? permissions.ask : [],
        allow: Array.isArray(permissions.allow) ? permissions.allow : [],
    };
    if (preservedDocument.permissions) {
        for (const key of ["disableBypassPermissionsMode", "disableAutoMode", "defaultMode", "deny", "ask", "allow"]) {
            delete preservedDocument.permissions[key];
        }
        if (!Object.keys(preservedDocument.permissions).length) delete preservedDocument.permissions;
    }
    next.managedRulesOnly = document?.allowManagedPermissionRulesOnly === true;
    delete preservedDocument.allowManagedPermissionRulesOnly;
    const autoMode = document?.autoMode ?? {};
    for (const [controlKey, { key }] of Object.entries(AUTO_MODE_LISTS)) {
        const entries = Array.isArray(autoMode[key]) ? autoMode[key] : [];
        next.autoMode[controlKey] = entries.filter((entry) => typeof entry === "string" && entry !== "$defaults");
        next.autoModeDefaults[controlKey] = !Array.isArray(autoMode[key]) || entries.includes("$defaults");
    }
    next.autoMode.classifyAllShell = autoMode.classifyAllShell === true;
    delete preservedDocument.autoMode;
    next.startingModel = typeof document?.model === "string" ? document.model : "";
    next.restrictModels = Array.isArray(document?.availableModels);
    next.availableModels = Array.isArray(document?.availableModels)
        ? document.availableModels.filter((value) => typeof value === "string")
        : [];
    delete preservedDocument.model;
    delete preservedDocument.availableModels;
    delete preservedDocument.enforceAvailableModels;
    if (Array.isArray(document?.allowedMcpServers)) {
        next.mcpServers = document.allowedMcpServers.map((item) => item?.serverName).filter(Boolean);
        next.mcpMode = document.allowManagedMcpServersOnly ? (next.mcpServers.length ? "allow" : "block") : "open";
    } else if (Array.isArray(document?.deniedMcpServers)) {
        next.mcpServers = document.deniedMcpServers.map((item) => item?.serverName).filter(Boolean);
        next.mcpMode = "deny";
    } else {
        next.mcpMode = "open";
    }
    delete preservedDocument.allowedMcpServers;
    delete preservedDocument.deniedMcpServers;
    delete preservedDocument.allowManagedMcpServersOnly;
    if (document?.managedMcpServers && typeof document.managedMcpServers === "object" && !Array.isArray(document.managedMcpServers)) {
        const names = Object.keys(document.managedMcpServers);
        const authorable = names.every((name) => {
            const entry = document.managedMcpServers[name];
            return entry && typeof entry === "object" && typeof entry.url === "string" && Object.keys(entry).every((key) => key === "url");
        });
        next.managedMcpServersAuthorable = authorable;
        if (authorable) {
            next.managedMcpServers = names.map((name) => ({ name, url: document.managedMcpServers[name].url }));
            delete preservedDocument.managedMcpServers;
        }
    } else if (document?.managedMcpServers !== undefined) {
        next.managedMcpServersAuthorable = false;
    } else {
        next.managedMcpServersAuthorable = true;
        delete preservedDocument.managedMcpServers;
    }
    if (Array.isArray(document?.strictKnownMarketplaces)) {
        const githubOnly = document.strictKnownMarketplaces.every((item) =>
            item?.source === "github"
            && typeof item.repo === "string"
            && Object.keys(item).every((key) => key === "source" || key === "repo"));
        if (githubOnly) {
            next.marketplaces = document.strictKnownMarketplaces.map((item) => item.repo);
            next.marketplaceMode = next.marketplaces.length ? "allow" : "block";
            delete preservedDocument.strictKnownMarketplaces;
        } else {
            next.marketplaceMode = "preserve";
        }
    } else {
        next.marketplaceMode = "open";
        delete preservedDocument.strictKnownMarketplaces;
    }
    if (Array.isArray(document?.blockedMarketplaces)) {
        const githubOnly = document.blockedMarketplaces.every((item) =>
            item?.source === "github"
            && typeof item.repo === "string"
            && Object.keys(item).every((key) => key === "source" || key === "repo"));
        next.blockedMarketplacesAuthorable = githubOnly;
        if (githubOnly) {
            next.blockedMarketplaces = document.blockedMarketplaces.map((item) => item.repo);
            delete preservedDocument.blockedMarketplaces;
        }
    } else {
        next.blockedMarketplacesAuthorable = true;
        delete preservedDocument.blockedMarketplaces;
    }
    const customizationRepresentable = Array.isArray(document?.strictPluginOnlyCustomization)
        && !document.strictPluginOnlyCustomization.includes("hooks");
    next.preserveCustomization = document?.strictPluginOnlyCustomization === true
        || (Array.isArray(document?.strictPluginOnlyCustomization) && !customizationRepresentable);
    next.customizationSurfaces = document?.strictPluginOnlyCustomization === true
        ? ["skills", "agents", "mcp"]
        : Array.isArray(document?.strictPluginOnlyCustomization)
            ? document.strictPluginOnlyCustomization.filter((surface) => surface !== "hooks")
            : [];
    if (!next.preserveCustomization) delete preservedDocument.strictPluginOnlyCustomization;
    if (!(next.mcpMode === "allow" || next.mcpMode === "deny") || !next.mcpServers.length) {
        next.customizationSurfaces = next.customizationSurfaces.filter((surface) => surface !== "mcp");
    }
    next.managedHooksOnly = document?.allowManagedHooksOnly === true;
    next.disableSkillShell = document?.disableSkillShellExecution === true;
    next.disableBundledSkills = document?.disableBundledSkills === true;
    next.disableWorkflows = document?.disableWorkflows === true;
    delete preservedDocument.allowManagedHooksOnly;
    delete preservedDocument.disableSkillShellExecution;
    delete preservedDocument.disableBundledSkills;
    delete preservedDocument.disableWorkflows;
    next.preserveLogin = Boolean(document?.forceLoginMethod && document.forceLoginMethod !== "claudeai");
    next.companyLogin = document?.forceLoginMethod === "claudeai";
    next.organizationIds = typeof document?.forceLoginOrgUUID === "string"
        ? [document.forceLoginOrgUUID]
        : Array.isArray(document?.forceLoginOrgUUID)
            ? document.forceLoginOrgUUID.filter((value) => typeof value === "string")
            : [];
    if (!next.preserveLogin) {
        delete preservedDocument.forceLoginMethod;
        delete preservedDocument.forceLoginOrgUUID;
    }
    next.disableRemoteControl = document?.disableRemoteControl === true;
    next.isolatePeerMachines = document?.isolatePeerMachines === true;
    next.sshHostAllowlist = Array.isArray(document?.sshHostAllowlist)
        ? document.sshHostAllowlist.filter((value) => typeof value === "string")
        : [];
    next.disableArtifact = document?.disableArtifact === true || document?.enableArtifact === false;
    next.disableBrowserExternalNavigation = document?.disableBrowserExternalNavigation === true;
    next.browserExternalPageTools = document?.browserExternalPageTools === "disabled" || document?.browserExternalPageTools === "disable";
    for (const key of ["disableRemoteControl", "isolatePeerMachines", "sshHostAllowlist", "disableArtifact", "enableArtifact", "disableBrowserExternalNavigation", "browserExternalPageTools"]) {
        delete preservedDocument[key];
    }
    next.allowedProviders = Array.isArray(document?.allowedProviders) ? document.allowedProviders.filter((value) => typeof value === "string") : [];
    next.forceLoginGatewayUrl = typeof document?.forceLoginGatewayUrl === "string" ? document.forceLoginGatewayUrl : "";
    next.deniedModels = Array.isArray(document?.deniedModels) ? document.deniedModels.filter((value) => typeof value === "string") : [];
    next.availableModelsMatch = document?.availableModelsMatch === "exact" ? "exact" : document?.availableModelsMatch === "prefix" ? "prefix" : "";
    next.minimumVersion = typeof document?.minimumVersion === "string" ? document.minimumVersion : "";
    for (const key of ["allowedProviders", "forceLoginGatewayUrl", "deniedModels", "availableModelsMatch", "minimumVersion"]) {
        delete preservedDocument[key];
    }
    next.allowAllClaudeAiMcps = document?.allowAllClaudeAiMcps === true;
    next.disableCommandPluginSources = document?.disableCommandPluginSources === true;
    next.disableSideloadFlags = document?.disableSideloadFlags === true;
    next.allowedHttpHookUrls = Array.isArray(document?.allowedHttpHookUrls) ? document.allowedHttpHookUrls.filter((value) => typeof value === "string") : [];
    next.httpHookAllowedEnvVars = Array.isArray(document?.httpHookAllowedEnvVars) ? document.httpHookAllowedEnvVars.filter((value) => typeof value === "string") : [];
    next.companyAnnouncements = Array.isArray(document?.companyAnnouncements) ? document.companyAnnouncements.filter((value) => typeof value === "string") : [];
    next.pluginTrustMessage = typeof document?.pluginTrustMessage === "string" ? document.pluginTrustMessage : "";
    for (const key of ["allowAllClaudeAiMcps", "disableCommandPluginSources", "disableSideloadFlags", "allowedHttpHookUrls", "httpHookAllowedEnvVars", "companyAnnouncements", "pluginTrustMessage"]) {
        delete preservedDocument[key];
    }
    next.preservedDocument = preservedDocument;
    return cloneControls(next);
}

export function changedTopLevelKeys(currentDocument, candidateDocument) {
    const keys = new Set([...Object.keys(currentDocument ?? {}), ...Object.keys(candidateDocument ?? {})]);
    return [...keys].filter(
        (key) => JSON.stringify(currentDocument?.[key]) !== JSON.stringify(candidateDocument?.[key]),
    );
}

export function policyForCategory(document, category) {
    const permissions = document?.permissions ?? {};
    if (category === "session") {
        return {
            permissions: {
                defaultMode: permissions.defaultMode,
                disableBypassPermissionsMode: permissions.disableBypassPermissionsMode,
            },
            model: document?.model,
            availableModels: document?.availableModels,
            enforceAvailableModels: document?.enforceAvailableModels,
            forceLoginMethod: document?.forceLoginMethod,
            forceLoginOrgUUID: document?.forceLoginOrgUUID,
            allowedProviders: document?.allowedProviders,
            forceLoginGatewayUrl: document?.forceLoginGatewayUrl,
            deniedModels: document?.deniedModels,
            availableModelsMatch: document?.availableModelsMatch,
            minimumVersion: document?.minimumVersion,
        };
    }
    if (category === "permissions") {
        return {
            permissions: {
                deny: permissions.deny,
                ask: permissions.ask,
                allow: permissions.allow,
            },
            allowManagedPermissionRulesOnly: document?.allowManagedPermissionRulesOnly,
        };
    }
    if (category === "auto") {
        return {
            permissions: { disableAutoMode: permissions.disableAutoMode },
            autoMode: document?.autoMode,
        };
    }
    if (category === "external") {
        return {
            disableRemoteControl: document?.disableRemoteControl,
            isolatePeerMachines: document?.isolatePeerMachines,
            sshHostAllowlist: document?.sshHostAllowlist,
            disableArtifact: document?.disableArtifact,
            enableArtifact: document?.enableArtifact,
            disableBrowserExternalNavigation: document?.disableBrowserExternalNavigation,
            browserExternalPageTools: document?.browserExternalPageTools,
        };
    }
    if (category === "messaging") {
        return {
            companyAnnouncements: document?.companyAnnouncements,
            pluginTrustMessage: document?.pluginTrustMessage,
        };
    }
    return {
        allowedMcpServers: document?.allowedMcpServers,
        deniedMcpServers: document?.deniedMcpServers,
        allowManagedMcpServersOnly: document?.allowManagedMcpServersOnly,
        managedMcpServers: document?.managedMcpServers,
        allowAllClaudeAiMcps: document?.allowAllClaudeAiMcps,
        strictKnownMarketplaces: document?.strictKnownMarketplaces,
        blockedMarketplaces: document?.blockedMarketplaces,
        disableCommandPluginSources: document?.disableCommandPluginSources,
        disableSideloadFlags: document?.disableSideloadFlags,
        strictPluginOnlyCustomization: document?.strictPluginOnlyCustomization,
        allowManagedHooksOnly: document?.allowManagedHooksOnly,
        allowedHttpHookUrls: document?.allowedHttpHookUrls,
        httpHookAllowedEnvVars: document?.httpHookAllowedEnvVars,
        disableSkillShellExecution: document?.disableSkillShellExecution,
        disableBundledSkills: document?.disableBundledSkills,
        disableWorkflows: document?.disableWorkflows,
    };
}

export function changedPolicyCategories(currentDocument, candidateDocument) {
    return POLICY_CATEGORIES
        .map((category) => category.id)
        .filter((category) => JSON.stringify(policyForCategory(currentDocument, category)) !== JSON.stringify(policyForCategory(candidateDocument, category)));
}
